import {
  getShareLinkPageMaxWidthStyle,
  type ShareLinkPageWidth,
} from '~/components/v2/components/shared/share-link-design/shareLinkPageWidth';

/*
 * Board share pages use the shared pageWidth mechanism — the `--share-link-page-max-width` var plus
 * a `max-w-[var(--share-link-page-max-width)]` column class — but with a different absent-value contract. The public board
 * page has always rendered its content column unbounded (full viewport width minus its fixed 8px
 * gutters), so links saved before the field existed must keep rendering unbounded; only an
 * explicitly stored `pageWidth` bounds and centers the column. This differs from the collection
 * page, whose column was born width-constrained and therefore falls back to PAGE_WIDTH_DEFAULT
 * ('wide') when the field is absent. Returning `undefined` here means "no var on the page root AND
 * no max-width classes on the column" — the renderer gates both on this one value, so a legacy
 * link's markup stays byte-identical. A stored value outside the compiled union is still a stored
 * value: it resolves through the shared helper's runtime-skew fallback to a real bounded width
 * rather than rendering an unbounded column the publisher explicitly opted out of.
 */
export const getBoardPageMaxWidthStyle = (
  pageWidth: ShareLinkPageWidth | undefined,
): ReturnType<typeof getShareLinkPageMaxWidthStyle> | undefined =>
  pageWidth === undefined ? undefined : getShareLinkPageMaxWidthStyle(pageWidth);

/*
 * Constrains the workspace header's inner content to the same centered column as the page content
 * (passed to the shared PublicWorkspaceHeader's contentClassName — the board-side analog of the
 * collection header's SHOWCASE_PAGE_COLUMN_CLASS_NAME reuse) while the header background stays
 * full-bleed. The 16px buffer mirrors the content row's px-2 gutters: below `pageWidth + 16px` the
 * clamped content column falls back to filling the row minus 8px per side, so the header content
 * clamps the same way to stay edge-aligned with it at every viewport. Only pass this when the link
 * stores a pageWidth (gate on getBoardPageMaxWidthStyle's result) — absent must add no wrapper to
 * the header at all, the same byte-identical legacy contract as the column above.
 */
export const BOARD_PAGE_HEADER_COLUMN_CLASS_NAME =
  'mx-auto w-full max-w-[min(100%_-_16px,var(--share-link-page-max-width))]';
